import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, KeyRound } from 'lucide-react';

import { supabase, isSupabaseConfigured } from '@/lib/supabase';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

export default function TechResetPasswordPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured for this environment.');
      setLoading(false);
      setReady(false);
      return;
    }

    supabase.auth
      .getSession()
      .then(({ data, error: sessionErr }) => {
        if (sessionErr) throw sessionErr;

        if (!data.session) {
          setError('Missing password recovery session. Please open the reset link from your email again.');
          setReady(false);
          return;
        }

        setReady(true);

        // Remove tokens from the URL hash for safety.
        if (typeof window !== 'undefined' && window.location.hash) {
          const cleanUrl = `${window.location.pathname}${window.location.search}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      })
      .catch((e) => {
        setError(getErrorMessage(e));
        setReady(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const next = password.trim();
    const confirm = confirmPassword.trim();

    if (next.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (next !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password: next });
      if (updateErr) throw updateErr;

      setPassword('');
      setConfirmPassword('');
      setSuccessMessage('Password updated. You can continue to the portal.');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-4">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Reset password</h1>
              <p className="text-gray-600 mt-2">Choose a new password for your technician account.</p>
            </div>

            {loading && <div className="text-sm text-gray-700">Loading…</div>}

            {error && (
              <div className="mb-5 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800 font-medium">{error}</p>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="mb-5 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl">
                <div className="text-sm text-green-900 font-medium">{successMessage}</div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800"
                  >
                    Go to dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 rounded-xl bg-white border border-gray-300 hover:bg-gray-50"
                  >
                    Back to login
                  </button>
                </div>
              </div>
            )}

            {ready && !successMessage && (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500 bg-gray-50 hover:bg-white"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    required
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500 bg-gray-50 hover:bg-white"
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                    required
                    disabled={submitting}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  disabled={submitting}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Updating…</span>
                      </>
                    ) : (
                      <span>Update password</span>
                    )}
                  </div>
                </button>
              </form>
            )}

            {!loading && !ready && !successMessage && (
              <div className="space-y-4">
                <div className="text-sm text-gray-700">
                  Please open the password reset link from your email again.
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 text-white hover:bg-gray-800"
                >
                  Back to login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
